import { BrowserRouter, Routes, Route } from "react-router";
import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from "react";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { AnnouncementBanner } from "@/components/ui/AnnouncementBanner";
import { Footer } from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/utilities/ScrollToTop";
import HomePage from "@/pages/HomePage";
import { CookieConsentInit } from "@/components/ui/CookieConsent";
import { PageViewTracker } from "@/components/utilities/PageViewTracker";
import { CtaClickTracker } from "@/components/utilities/CtaClickTracker";

const PlatformPage = lazy(() => import("@/pages/PlatformPage"));
const FederatedSiemPage = lazy(() => import("@/pages/platform/FederatedSiemPage"));
const EndpointControlPage = lazy(() => import("@/pages/platform/EndpointControlPage"));
const ThreatHuntingPage = lazy(() => import("@/pages/platform/ThreatHuntingPage"));
const AlertTriagePage = lazy(() => import("@/pages/platform/AlertTriagePage"));
const DetectionEngineeringPage = lazy(() => import("@/pages/platform/DetectionEngineeringPage"));
const ThreatIntelligencePage = lazy(() => import("@/pages/platform/ThreatIntelligencePage"));
const ScoutPage = lazy(() => import("@/pages/platform/ScoutPage"));
const ThreatHuntersPage = lazy(() => import("@/pages/solutions/ThreatHuntersPage"));
const SocPage = lazy(() => import("@/pages/solutions/SocPage"));
const IncidentResponsePage = lazy(() => import("@/pages/solutions/IncidentResponsePage"));
const DetectionEngineersPage = lazy(() => import("@/pages/solutions/DetectionEngineeringPage"));
const MsspPage = lazy(() => import("@/pages/solutions/MsspPage"));
const DemoPage = lazy(() => import("@/pages/DemoPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const CompanyPage = lazy(() => import("@/pages/CompanyPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const CookiesPage = lazy(() => import("@/pages/CookiesPage"));
const BeenBreachedPage = lazy(() => import("@/pages/BeenBreachedPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const TrustCenterPage = lazy(() => import("@/pages/TrustCenterPage"));
const AcceptableUsePage = lazy(() => import("@/pages/trust-center/AcceptableUsePage"));
const DataProcessingAddendumPage = lazy(
  () => import("@/pages/trust-center/DataProcessingAddendumPage")
);
const SecurityDisclosurePage = lazy(() => import("@/pages/trust-center/SecurityDisclosurePage"));
const SubprocessorsPage = lazy(() => import("@/pages/trust-center/SubprocessorsPage"));
const NoticesPage = lazy(() => import("@/pages/trust-center/NoticesPage"));
// Search-intent pages
const GlossaryPage = lazy(() => import("@/pages/GlossaryPage"));
const CompareIndexPage = lazy(() => import("@/pages/compare/CompareIndexPage"));
const ComparisonPage = lazy(() => import("@/pages/compare/ComparisonPage"));
const AiSocAnalystPage = lazy(() => import("@/pages/AiSocAnalystPage"));
const SigmaRuleTestingPage = lazy(() => import("@/pages/SigmaRuleTestingPage"));
const OpenSourcePage = lazy(() => import("@/pages/OpenSourcePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Expose Lenis instance globally so ScrollToTop can use it
declare global {
  interface Window {
    __lenis?: Lenis;
  }
}

// The prerender (src/entry-server.tsx) swaps in a StaticRouter; browsers use BrowserRouter.
function App({ Router = BrowserRouter }: { Router?: ComponentType<{ children: ReactNode }> }) {
  useEffect(() => {
    // Skip smooth scrolling on touch devices — native scrolling feels better
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
    if (isTouchDevice) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    window.__lenis = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  // Start analytics + HubSpot when cookie consent is granted, and stop them
  // when it is withdrawn — without waiting for a reload.
  // Dynamic imports keep posthog-js and hubspot out of the main bundle.
  useEffect(() => {
    const initServices = async () => {
      const [{ syncAnalyticsConsent }, { syncHubSpotConsent }] = await Promise.all([
        import("@/lib/analytics"),
        import("@/lib/hubspot"),
      ]);
      syncAnalyticsConsent();
      syncHubSpotConsent();
    };

    initServices();

    window.addEventListener("cookieConsentChanged", initServices);
    return () => window.removeEventListener("cookieConsentChanged", initServices);
  }, []);

  // reducedMotion="user": every framer-motion animation honours the visitor's
  // prefers-reduced-motion setting (transforms and layout animation are skipped).
  // Safe in the prerender: the media query is only read in the browser.
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <ScrollToTop />
        <PageViewTracker />
        <CtaClickTracker />
        <AnnouncementBanner />
        <Navbar />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/platform/federated-siem" element={<FederatedSiemPage />} />
            <Route path="/platform/endpoint-control" element={<EndpointControlPage />} />
            <Route path="/platform/threat-hunting" element={<ThreatHuntingPage />} />
            <Route path="/platform/alert-triage" element={<AlertTriagePage />} />
            <Route path="/platform/detection-engineering" element={<DetectionEngineeringPage />} />
            <Route path="/platform/threat-intelligence" element={<ThreatIntelligencePage />} />
            <Route path="/platform/scout" element={<ScoutPage />} />
            <Route path="/solutions/threat-hunters" element={<ThreatHuntersPage />} />
            <Route path="/solutions/soc" element={<SocPage />} />
            <Route path="/solutions/incident-response" element={<IncidentResponsePage />} />
            <Route path="/solutions/detection-engineering" element={<DetectionEngineersPage />} />
            <Route path="/solutions/mssp" element={<MsspPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/company" element={<CompanyPage />} />
            <Route path="/been-breached" element={<BeenBreachedPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/cookies" element={<CookiesPage />} />
            <Route path="/legal/acceptable-use" element={<AcceptableUsePage />} />
            <Route path="/legal/dpa" element={<DataProcessingAddendumPage />} />
            <Route path="/legal/security-disclosure" element={<SecurityDisclosurePage />} />
            <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />
            <Route path="/legal/notices" element={<NoticesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/trust-center" element={<TrustCenterPage />} />
            {/* Search-intent pages */}
            <Route path="/glossary" element={<GlossaryPage />} />
            <Route path="/compare" element={<CompareIndexPage />} />
            <Route
              path="/compare/huntbase-vs-splunk"
              element={<ComparisonPage slug="huntbase-vs-splunk" />}
            />
            <Route
              path="/compare/huntbase-vs-crowdstrike-overwatch"
              element={<ComparisonPage slug="huntbase-vs-crowdstrike-overwatch" />}
            />
            <Route
              path="/compare/huntbase-vs-dropzone-ai"
              element={<ComparisonPage slug="huntbase-vs-dropzone-ai" />}
            />
            <Route
              path="/compare/huntbase-vs-tines"
              element={<ComparisonPage slug="huntbase-vs-tines" />}
            />
            <Route path="/ai-soc-analyst" element={<AiSocAnalystPage />} />
            <Route path="/sigma-rule-testing" element={<SigmaRuleTestingPage />} />
            <Route path="/open-source" element={<OpenSourcePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <Footer />
        <CookieConsentInit />
      </Router>
    </MotionConfig>
  );
}

export default App;
